import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { ApiError } from '@/api/httpClient'
import { useProjectsStore } from '@/stores/projectsStore'
import { morphToast } from '@/components/ui/Toast'
import type { ProjectListItem } from '@/api/types'

export function EditProjectModal({
  project,
  onClose,
}: {
  project: ProjectListItem | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const rename = useProjectsStore((s) => s.rename)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    if (!project) return
    setName(project.name)
    setNameError(null)
  }, [project])

  const normalizedName = useMemo(() => name.trim(), [name])

  async function handleSave() {
    if (!project) return
    if (!normalizedName) {
      setNameError(t('newProject.nameRequired'))
      return
    }
    setSubmitting(true)
    try {
      await rename(project.id, normalizedName)
      morphToast.success(t('common.saved', 'Salvo.'))
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PROJECT_NAME_DUPLICATE') {
        setNameError(t('newProject.duplicate', 'Já existe um projeto com este nome neste workspace.'))
        return
      }
      morphToast.error(err instanceof Error ? err.message : t('common.error', 'Erro ao salvar'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={project !== null}
      title={t('project.editTitle', 'Editar projeto')}
      onClose={onClose}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} disabled={submitting}>
            {submitting ? t('common.loading', 'Aguarde...') : t('common.save')}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('newProject.nameLabel')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameError(null)
            }}
            className="morph-input"
            disabled={submitting}
          />
          {nameError ? (
            <div className="mt-1 text-[11px]" style={{ color: 'var(--color-danger)' }}>
              {nameError}
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  )
}
